import { useMutation } from '@apollo/client';
import { EVisibility, WorldMapInput } from '__generated__/globalTypes';
import { MapCountryFillSubsection } from 'components/minute/sidebar/MapSidebar/MapSettingsSection/MapCountryFillSubsection';
import { MapInitialPositionSubsection } from 'components/minute/sidebar/MapSidebar/MapSettingsSection/MapInitialPositionSubsection';
import { MapSettingsStyleSubsection } from 'components/minute/sidebar/MapSidebar/MapSettingsSection/MapSettingsStyleSubsection';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { CheckValue } from 'elements/minute/sidebar/sidebarComponents/atomic/CheckValue';
import { Message } from 'elements/minute/sidebar/sidebarComponents/atomic/Message';
import { NumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/NumberValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import {
    UpdateWorldMap,
    UpdateWorldMapVariables
} from 'graphql/mutation/__generated__/UpdateWorldMap';
import { UPDATE_WORLD_MAP_MUTATION } from 'graphql/mutation/worldMutation';
import { useAccess } from 'hooks/access/useAccess';
import { useDeleteMap } from 'hooks/map/useDeleteMap';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal } from 'session/ctxModal';
import { BLANK_STYLE } from 'static/config/stylesConfig';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';
import { PartialDeep } from 'type-fest';

interface Props {
    className?: string;
}

export const MapSettingsSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const ctxModal = useContext(CtxModal);
    const navigate = useNavigate();
    const deleteMap = useDeleteMap();
    const access = useAccess();

    const isImageMap = ctxEdit.map.defaultStyles[0] == BLANK_STYLE;

    const { getTokenForMapId, removeTokenForMapId } = useWorldMapLocalStorage();
    const MAP_VISIBILITY_OPTIONS = [
        { text: Localization.PUBLIC, value: EVisibility.PUBLIC },
        { text: Localization.PRIVATE, value: EVisibility.PRIVATE }
    ];

    const [sendMutation] = useMutation<UpdateWorldMap, UpdateWorldMapVariables>(
        UPDATE_WORLD_MAP_MUTATION
    );

    const updateWorldMap = (data: PartialDeep<WorldMapInput>) => {
        sendMutation({
            variables: {
                worldMapId: ctxEdit.map?.worldMapId,
                token: getTokenForMapId(ctxEdit.map?.worldMapId),
                map: data
            }
        });
    };

    function changeInitial(
        values: Partial<{ minZoom: number; maxZoom: number; rotation: number; pitch: number }>
    ) {
        ctxEdit.setMap({
            ...ctxEdit.map,
            initial: {
                ...ctxEdit.map.initial,
                ...values
            },
            initialMobile: {
                ...ctxEdit.map.initialMobile,
                ...values
            }
        });

        updateWorldMap({
            initial: values,
            initialMobile: values
        });
    }

    function changeShowOnMap(value: number) {
        ctxEdit.setMap({
            ...ctxEdit.map,
            showOnMapZoom: value
        });

        updateWorldMap({
            showOnMapZoom: value
        });
    }

    async function del() {
        ctxModal.setModal({
            type: EModal.CONFIRM,
            data: {
                text: Localization.DELETE_MAP_CONFIRM,
                onConfirm: () => {
                    go();
                }
            } as IConfirmActionModal
        });

        async function go() {
            await deleteMap.del(ctxEdit.map.worldMapId);
            removeTokenForMapId(ctxEdit.map.worldMapId);
            userMessage(EToastType.SUCCESS, Localization.MAP_DELETED);
            navigate('/admin');
        }
    }

    return (
        <S_MapSettingsSection className={props.className}>
            <SidebarSection title={Localization.MAP_SETTINGS} icon={{ iconName: 'gear' }}>
                {/* <SidebarSubsection title={Localization.PRIVACY} icon={{ iconName: 'eye' }}>
                    <SidebarContent>
                        <OptionRowValue
                            items={MAP_VISIBILITY_OPTIONS}
                            selectedIndex={MAP_VISIBILITY_OPTIONS.findIndex(
                                (e) => e.value === ctxEdit.map.visibility
                            )}
                            onSelected={(index) => {
                                const visibility = MAP_VISIBILITY_OPTIONS[index].value;
                                ctxEdit.updateMap({ visibility });
                            }}
                        />
                        {ctxEdit.map.visibility === EVisibility.PRIVATE && (
                            <S_Message type="info">{Localization.PRIVACY_DESC}</S_Message>
                        )}
                    </SidebarContent>
                </SidebarSubsection> */}

                <SidebarSubsection
                    title={Localization.GPS_POSITION}
                    icon={{ iconName: 'crosshairs' }}
                >
                    <SidebarContent>
                        <CheckValue
                            value={ctxEdit.map.allowGeolocation}
                            label={
                                ctxEdit.map.allowGeolocation
                                    ? Localization.ENABLED
                                    : Localization.DISABLED
                            }
                            onChange={(value) => {
                                ctxEdit.updateMap({ allowGeolocation: value });
                            }}
                        />
                    </SidebarContent>
                </SidebarSubsection>

                <MapSettingsStyleSubsection />

                {!isImageMap && access.isExperimental && <MapCountryFillSubsection />}
                <SidebarSubsection
                    title={Localization.MAP_ORIENTATION}
                    icon={{ iconName: 'angle' }}
                >
                    <SidebarContent>
                        <SidebarSectionRow title={Localization.MAP_ROTATION}>
                            <NumberValue
                                value={ctxEdit.map.initial?.rotation}
                                onChange={(e) => {
                                    changeInitial({ rotation: e });
                                }}
                            />
                        </SidebarSectionRow>
                        <SidebarSectionRow title={Localization.PITCH_ANGLE}>
                            <NumberValue
                                value={ctxEdit.map.initial?.pitch}
                                onChange={(e) => {
                                    changeInitial({ pitch: e });
                                }}
                            />
                        </SidebarSectionRow>
                    </SidebarContent>
                </SidebarSubsection>

                <MapInitialPositionSubsection />
                <SidebarSubsection
                    title={Localization.ZOOM_SETTINGS}
                    icon={{ iconName: 'magnifying-glass-plus' }}
                >
                    <SidebarContent>
                        <SidebarSectionRow title={Localization.SHOW_ON_ZOOM}>
                            <NumberValue
                                value={ctxEdit.map.showOnMapZoom}
                                onChange={(e) => {
                                    changeShowOnMap(e);
                                }}
                            />
                        </SidebarSectionRow>
                        <SidebarSectionRow title={Localization.MIN_MAX_ZOOM}>
                            <NumberValue
                                value={ctxEdit.map.initial.minZoom}
                                onChange={(e) => {
                                    changeInitial({ minZoom: e });
                                }}
                            />
                            <NumberValue
                                value={ctxEdit.map.initial.maxZoom}
                                onChange={(e) => {
                                    changeInitial({ maxZoom: e });
                                }}
                            />
                        </SidebarSectionRow>
                    </SidebarContent>
                </SidebarSubsection>
                <SidebarSubsection
                    title={Localization.DELETE_MAP}
                    icon={{ iconName: 'trash-can' }}
                    disableBottomBorder
                >
                    <SidebarContent>
                        <S_ActionButton
                            text={Localization.DELETE}
                            onClick={() => {
                                del();
                            }}
                        />
                    </SidebarContent>
                </SidebarSubsection>
            </SidebarSection>
        </S_MapSettingsSection>
    );
};

const S_MapSettingsSection = styled.div``;

const S_Message = styled(Message)`
    margin: 15px 0 0 0;
`;

const S_ActionButton = styled(ActionButton)`
    width: fit-content;
`;
